/**
 * Sidebar Component - AWLP Design System
 * Collapsible navigation sidebar with role-based menu filtering
 */

import {
  Activity,
  Building2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileText,
  Film,
  HardDrive,
  Home,
  Inbox,
  Key,
  LayoutDashboard,
  ListChecks,
  Mail,
  MapPin,
  Receipt,
  Settings,
  Shield,
  Target,
  UserCheck,
  Users,
  UserSearch,
  Workflow,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { WORKFLOW_ROUTES } from '@upskill/shared';

import { useWorkflowDefinitions } from '../../../hooks/useWorkflowDefinitions';
import { useNeedsAssignmentCount } from '../../../hooks/useRedDotAssignment';
import { useSupportAdminUnreadCount, usePlatformSupportUnreadCount } from '../../../hooks/useSupportQueue';
import { useAuthStore } from '../../../stores/authStore';
import { useUIStore } from '../../../stores/uiStore';
import { Badge } from '../../ui/Badge';

export interface NavSubItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

export interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  permissions?: string[];
  badge?: string | number;
  superAdminOnly?: boolean;
  end?: boolean;
  subItems?: NavSubItem[];
}

const Container = styled.aside<{ $isCollapsed: boolean; $isMobileOpen: boolean }>`
  position: sticky;
  top: ${({ theme }) => theme.layout.header.height};
  height: calc(100vh - ${({ theme }) => theme.layout.header.height});
  width: ${({ theme, $isCollapsed }) =>
    $isCollapsed ? theme.layout.sidebar.widthCollapsed : theme.layout.sidebar.width};
  background: ${({ theme }) => theme.colors.gradients.sidebar};
  transition:
    width ${({ theme }) => theme.transitions.base},
    left ${({ theme }) => theme.transitions.base};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: ${({ theme }) => theme.zIndex.sticky};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: fixed;
    left: ${({ $isMobileOpen }) => ($isMobileOpen ? '0' : '-256px')};
    width: ${({ theme }) => theme.layout.sidebar.width};
    z-index: ${({ theme }) => theme.zIndex.modal};
  }
`;

const MobileOverlay = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
    position: fixed;
    top: ${({ theme }) => theme.layout.header.height};
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: ${({ theme }) => theme.zIndex.fixed};
  }
`;

const Nav = styled.nav`
  flex: 1;
  padding: ${({ theme }) => theme.spacing[6]} 0;
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: ${({ theme }) => theme.borderRadius.md};

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

// No margin-bottom: the gap between sections is governed entirely by Divider's
// own vertical margin below, so it stays a single, consistent value regardless
// of whether the preceding section's items are expanded or collapsed.
const NavSection = styled.div`
  padding: 0 ${({ theme }) => theme.spacing[3]};
`;

const SectionTitle = styled.h3<{ $isCollapsed: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary.lightSky};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
  opacity: ${({ $isCollapsed }) => ($isCollapsed ? 0 : 1)};
  transition: opacity ${({ theme }) => theme.transitions.fast};

  /* Icon-only sidebar mode: remove from layout entirely (not just faded) so it
     doesn't reserve height or stay focusable/in the a11y tree. */
  display: ${({ $isCollapsed }) => ($isCollapsed ? 'none' : 'block')};
`;

// Clickable header row wrapping SectionTitle + collapse chevron. In icon-only
// sidebar mode there's no title text to click, so the whole header is hidden.
const SectionHeader = styled.button<{ $isCollapsed: boolean }>`
  display: ${({ $isCollapsed }) => ($isCollapsed ? 'none' : 'flex')};
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  background: none;
  border: none;
  font-family: inherit;
  cursor: pointer;

  &:hover ${SectionTitle} {
    color: ${({ theme }) => theme.colors.text.white};
  }
`;

const SectionChevron = styled.span<{ $collapsed: boolean; $isCollapsed: boolean }>`
  display: ${({ $isCollapsed }) => ($isCollapsed ? 'none' : 'inline-flex')};
  color: ${({ theme }) => theme.colors.primary.lightSky};
  opacity: 0.75;
  transition: transform ${({ theme }) => theme.transitions.fast};
  transform: rotate(${({ $collapsed }) => ($collapsed ? '-90deg' : '0deg')});

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Shared visual styles for every nav item (base + hover + collapsed).
const navItemBaseCss = css<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.primary.lightSky};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.fast};
  position: relative;
  white-space: nowrap;

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: ${({ theme }) => theme.colors.text.white};
  }

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      justify-content: center;

      span:not([role='status']) {
        display: none;
      }
    `}
`;

// Active appearance, shared by the pathname-based NavLink (.active) and the
// query-aware Link variant ($active) below.
const activeNavCss = css`
  background: rgba(255, 255, 255, 0.15);
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const NavItemLink = styled(NavLink)<{ $isCollapsed: boolean }>`
  ${navItemBaseCss}

  &.active {
    ${activeNavCss}
  }
`;

// For nav items that share a pathname and differ only by query string (the
// Insights reports at /analytics?report=…). NavLink's auto-active matches on
// pathname ONLY, so it would highlight every such item at once. We render a plain
// Link and drive the active state ourselves via $active so exactly one lights up.
const NavItemQueryLink = styled(Link)<{ $isCollapsed: boolean; $active: boolean }>`
  ${navItemBaseCss}

  ${({ $active }) => $active && activeNavCss}
`;

const NavItemBadge = styled(Badge)`
  margin-left: auto;
`;

// Flyout submenu — uses position:fixed so sidebar overflow:hidden cannot clip it.
// Top/left are set via inline style at render time from the anchor ref's getBoundingClientRect.
const FlyoutMenu = styled.ul<{ $visible: boolean }>`
  position: fixed;
  list-style: none;
  margin: 0;
  padding: ${({ theme }) => theme.spacing[2]} 0;
  background: ${({ theme }) => theme.colors.gradients.sidebar};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  min-width: 220px;
  z-index: ${({ theme }) => theme.zIndex.popover};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) => ($visible ? 'translateX(0)' : 'translateX(-6px)')};
  transition:
    opacity ${({ theme }) => theme.transitions.fast},
    transform ${({ theme }) => theme.transitions.fast};
`;

const FlyoutItem = styled.li`
  button {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[3]};
    width: 100%;
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.primary.lightSky};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    font-family: inherit;
    white-space: nowrap;
    transition:
      background ${({ theme }) => theme.transitions.fast},
      color ${({ theme }) => theme.transitions.fast};

    &:hover,
    &:focus-visible {
      background: rgba(255, 255, 255, 0.1);
      color: ${({ theme }) => theme.colors.text.white};
      outline: none;
    }
  }

  /* Match the main-nav icon sizing. */
  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

// Chevron on a nav item that has a submenu — signals "hover to reveal".
const SubmenuChevron = styled.span`
  margin-left: auto;
  display: inline-flex;
  color: ${({ theme }) => theme.colors.primary.lightSky};
  opacity: 0.75;

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Wrapper provides a relative anchor for the trigger item (flyout itself is fixed).
const FlyoutWrapper = styled.div`
  position: relative;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[3]};
`;

// App version pinned at the extreme bottom of the sidebar. Built from package.json
// at build time via the vite `define` (__APP_VERSION__); `typeof` guard keeps it
// safe in test/SSR contexts where the define is absent.
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '';

const SidebarFooter = styled.div`
  flex-shrink: 0;
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: center;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.primary.lightSky};
  opacity: 0.7;
  white-space: nowrap;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

// Top-level items (no section header) — always-relevant personal entries.
// ADO 564: /my-tasks was routed and working but linked from nowhere, so the only
// route to your own assigned work was a notification — and notifications age out
// of the tray. Everyone holding `tasks:my_tasks` gets the entry.
const topNavItems: NavItem[] = [
  { to: '/dashboard', icon: <Home />, label: 'Dashboard', permissions: ['org:dashboard'] },
  { to: '/my-tasks', icon: <ListChecks />, label: 'My Tasks', permissions: ['tasks:my_tasks'] },
];

// Organization — org-admin managed surfaces.
const organizationNavItems: NavItem[] = [
  { to: '/clients', icon: <UserCheck />, label: 'Clients', permissions: ['clients:manage'] },
  { to: '/team', icon: <Users />, label: 'Team', permissions: ['team:manage'] },
  { to: '/locations', icon: <MapPin />, label: 'Centers', permissions: ['locations:manage'] },
  { to: '/goals', icon: <Target />, label: 'Goals', permissions: ['goals:manage'] },
  { to: '/roles', icon: <Shield />, label: 'Roles', permissions: ['role:extend'] },
];

// System — super admin / platform operations. Each option has its own coarse
// `*:manage` permission (super admins always see them via the isSuperAdmin
// short-circuit in filterNavItems).
const systemNavItems: NavItem[] = [
  {
    to: '/platform',
    icon: <LayoutDashboard />,
    label: 'Platform Home',
    permissions: ['platform_dashboard:manage'],
    end: true,
  },
  {
    to: '/platform/organizations',
    icon: <Building2 />,
    label: 'Organizations',
    permissions: ['organizations:manage'],
  },
  {
    to: '/platform/registration-requests',
    icon: <ClipboardList />,
    label: 'Requests',
    permissions: ['requests:manage'],
  },
  {
    to: '/platform/users',
    icon: <UserSearch />,
    label: 'User Lookup',
    permissions: ['user_lookup:manage'],
  },
  {
    to: '/platform/subscriptions',
    icon: <CreditCard />,
    label: 'Subscriptions',
    permissions: ['subscriptions:manage'],
  },
  {
    to: '/platform/invoices',
    icon: <Receipt />,
    label: 'Invoices',
    permissions: ['invoices:manage'],
  },
  {
    to: '/platform/email-logs',
    icon: <Mail />,
    label: 'Email Logs',
    permissions: ['email_logs:manage'],
  },
  { to: '/roles', icon: <Shield />, label: 'Roles', permissions: ['roles:manage'] },
  { to: '/permissions', icon: <Key />, label: 'Permissions', permissions: ['permissions:manage'] },
  {
    to: '/admin/file-assets',
    icon: <HardDrive />,
    label: 'File Assets',
    permissions: ['file_assets:manage'],
  },
  { to: '/admin/resources', icon: <Film />, label: 'Resources', superAdminOnly: true },
  { to: '/admin/plans', icon: <CreditCard />, label: 'Plans', permissions: ['plans:manage'] },
];

const settingsNavItems: NavItem[] = [
  { to: '/settings', icon: <Settings />, label: 'Settings', permissions: ['profile:manage'] },
];

// Per-workflow icon (code → icon); unknown workflows fall back to a generic icon.
const WORKFLOW_ICONS: Record<string, React.ReactNode> = {
  red_dot: <Activity />,
  preference_assessment: <Target />,
};

// Unified "report/document" icon for Insights items — deliberately distinct from
// the Workflows section's per-workflow icons (e.g. Red Dot's <Activity />) so the
// two sections read as visually different at a glance.
const REPORT_ICON = <FileText />;

// ============================================================================
// NavItemWithFlyout — wraps a nav item that has subItems with a hover flyout.
// The FlyoutMenu uses position:fixed so sidebar overflow:hidden cannot clip it.
// ============================================================================

interface NavItemWithFlyoutProps {
  item: NavItem;
  isCollapsed: boolean;
}

// Merged open+position into one state object so they update atomically and
// never show the menu at (0,0) before the first measurement.
interface FlyoutState {
  open: boolean;
  top: number;
  left: number;
}

const NavItemWithFlyout = ({ item, isCollapsed }: NavItemWithFlyoutProps) => {
  const navigate = useNavigate();
  const [flyout, setFlyout] = useState<FlyoutState>({ open: false, top: 0, left: 0 });
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => setFlyout((prev) => ({ ...prev, open: false })), 120);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const handleAnchorEnter = useCallback(() => {
    cancelClose();
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setFlyout({ open: true, top: rect.top, left: rect.right + 4 });
    }
  }, [cancelClose]);

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const subItems = item.subItems ?? [];

  return (
    <FlyoutWrapper ref={anchorRef} onMouseEnter={handleAnchorEnter} onMouseLeave={scheduleClose}>
      <NavItemLink to={item.to} end={item.end} $isCollapsed={isCollapsed}>
        {item.icon}
        <span>{item.label}</span>
        {!isCollapsed && subItems.length > 0 && (
          <SubmenuChevron aria-hidden="true">
            <ChevronRight />
          </SubmenuChevron>
        )}
      </NavItemLink>

      <FlyoutMenu
        $visible={flyout.open}
        style={{ top: flyout.top, left: flyout.left }}
        role="menu"
        aria-label={`${item.label} submenu`}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        {subItems.map((sub) => (
          <FlyoutItem key={sub.id} role="none">
            {/* Native <button> fires onClick on Enter/Space — no onKeyDown needed */}
            <button
              role="menuitem"
              onClick={() => {
                setFlyout((prev) => ({ ...prev, open: false }));
                navigate(`${item.to}?report=${sub.id}`);
              }}
            >
              {sub.icon}
              <span>{sub.label}</span>
            </button>
          </FlyoutItem>
        ))}
      </FlyoutMenu>
    </FlyoutWrapper>
  );
};

export const Sidebar = () => {
  const { isSidebarCollapsed, isMobileSidebarOpen, closeMobileSidebar, collapsedSections, toggleSection } =
    useUIStore();
  const { hasAnyPermission, isSuperAdmin, hasWorkflowAccess, hasPermission } = useAuthStore();
  const location = useLocation();
  const { data: workflowDefs } = useWorkflowDefinitions();

  // Close mobile sidebar on route change
  useEffect(() => {
    closeMobileSidebar();
  }, [location.pathname, closeMobileSidebar]);

  // Purely permission-driven. Super admins are a platform persona whose grants
  // (System keys + profile:manage) naturally limit them to the System + Settings
  // sections — they do NOT auto-see org surfaces.
  const filterNavItems = (items: NavItem[]): NavItem[] => {
    return items.filter((item) => {
      // Legacy super-admin-only items.
      if (item.superAdminOnly) return isSuperAdmin();
      // No permissions required — visible to everyone.
      if (!item.permissions || item.permissions.length === 0) return true;
      // Show if user has any of the required permissions.
      return hasAnyPermission(item.permissions);
    });
  };

  // Workflows section — dynamic, from the workflow-definitions catalog, filtered
  // to those the user has at least Execute access to. New workflows appear here
  // automatically once they have a route mapping.
  const workflowNavItems: NavItem[] = (workflowDefs ?? [])
    .filter((d) => d.isActive && WORKFLOW_ROUTES[d.code] && hasWorkflowAccess(d.code))
    .map((d) => ({
      to: WORKFLOW_ROUTES[d.code],
      icon: WORKFLOW_ICONS[d.code] ?? <Workflow />,
      label: d.name,
    }));

  // Insights section — direct per-workflow report links (no submenu).
  // Red Dot Report requires red_dot:manage (RBTs with execute-only are excluded).
  // Cross-workflow reports (Fidelity, Task Completion, Clinician Performance) will be added later.
  const insightsNavItems: NavItem[] = [];
  if (hasPermission('red_dot:manage')) {
    insightsNavItems.push({
      to: '/analytics?report=red-dot',
      icon: REPORT_ICON,
      label: 'Red Dot',
    });
  }

  const filteredTopNav = filterNavItems(topNavItems);
  // Action Center (ADO 538 follow-up): the Org Admin inbox. Built in-component
  // (not in the static array) because its badge is a live pending-actions count.
  // The count query only runs for permitted users — the endpoint 403s otherwise.
  // Deliberately NOT shown to platform super admins: like every other
  // Organization item (hasAnyPermission has no super-admin bypass), this is an
  // org-scoped operational surface — super admins work from the System section
  // and reach org data via impersonation. The backend guard still admits them
  // by direct URL.
  // The support queue lives INSIDE the Action Center as a second section, so
  // there is one nav entry, admitted by either permission, with a badge that
  // sums both sections' pending work. Each count query stays gated on its own
  // permission so it never fires (and never 403s) for a viewer who can't see
  // that section.
  const canSeeNeedsAssignment = hasAnyPermission(['action_center:full']);
  const canSeeSupportQueue = hasAnyPermission(['support_requests:manage']);
  const canSeeActionCenter = canSeeNeedsAssignment || canSeeSupportQueue;

  const { data: pendingActionCount } = useNeedsAssignmentCount({
    enabled: canSeeNeedsAssignment,
  });
  const { data: supportUnreadCount } = useSupportAdminUnreadCount({ enabled: canSeeSupportQueue });
  const actionCenterCount = (pendingActionCount ?? 0) + (supportUnreadCount ?? 0);

  const actionCenterItem: NavItem[] = canSeeActionCenter
    ? [
        {
          to: '/action-center',
          icon: <Inbox />,
          label: 'Action Center',
          badge: actionCenterCount > 0 ? actionCenterCount : undefined,
        },
      ]
    : [];

  // Platform Action Center (Super Admin) — the platform-lane support queue in
  // the same stacked-sections inbox, visible to super admins only, matching the
  // other System items' superAdminOnly convention.
  const { data: platformSupportUnreadCount } = usePlatformSupportUnreadCount({
    enabled: isSuperAdmin(),
  });
  const platformSupportItem: NavItem[] = isSuperAdmin()
    ? [
        {
          to: '/platform/action-center',
          icon: <Inbox />,
          label: 'Action Center',
          badge:
            platformSupportUnreadCount && platformSupportUnreadCount > 0
              ? platformSupportUnreadCount
              : undefined,
        },
      ]
    : [];

  const filteredOrgNav = [...actionCenterItem, ...filterNavItems(organizationNavItems)];
  const filteredSystemNav = [...filterNavItems(systemNavItems), ...platformSupportItem];
  const filteredSettingsNav = filterNavItems(settingsNavItems);

  const renderItem = (item: NavItem) => {
    if (item.subItems && item.subItems.length > 0) {
      return <NavItemWithFlyout key={item.to} item={item} isCollapsed={isSidebarCollapsed} />;
    }
    // Query-string items (same pathname, different ?report=) — NavLink's auto-active
    // matches on pathname only, so it would light up every Insights report at once.
    // Drive active ourselves from the matching query param instead.
    if (item.to.includes('?')) {
      const [path, queryString] = item.to.split('?');
      const itemReport = new URLSearchParams(queryString).get('report');
      const isActive =
        location.pathname === path &&
        new URLSearchParams(location.search).get('report') === itemReport;
      return (
        <NavItemQueryLink
          key={item.to}
          to={item.to}
          $active={isActive}
          $isCollapsed={isSidebarCollapsed}
        >
          {item.icon}
          <span>{item.label}</span>
          {item.badge && Number(item.badge) > 0 && !isSidebarCollapsed && (
            <NavItemBadge variant="primary">{item.badge}</NavItemBadge>
          )}
        </NavItemQueryLink>
      );
    }
    return (
      <NavItemLink key={item.to} to={item.to} end={item.end} $isCollapsed={isSidebarCollapsed}>
        {item.icon}
        <span>{item.label}</span>
        {item.badge && Number(item.badge) > 0 && !isSidebarCollapsed && (
          <NavItemBadge variant="primary">{item.badge}</NavItemBadge>
        )}
      </NavItemLink>
    );
  };

  // A section is collapsed only when the sidebar itself is expanded AND the user
  // has toggled it closed. In icon-only sidebar mode there's no header text to
  // click, so sections always behave as expanded internally.
  const isSectionCollapsed = (title: string) => !isSidebarCollapsed && !!collapsedSections[title];

  const renderSection = (title: string, items: NavItem[], withDivider = true) => {
    if (items.length === 0) return null;
    const sectionCollapsed = isSectionCollapsed(title);
    return (
      <>
        {withDivider && <Divider />}
        <NavSection>
          <SectionHeader
            type="button"
            $isCollapsed={isSidebarCollapsed}
            onClick={() => toggleSection(title)}
            aria-expanded={!sectionCollapsed}
          >
            <SectionTitle $isCollapsed={isSidebarCollapsed}>{title}</SectionTitle>
            <SectionChevron
              $collapsed={sectionCollapsed}
              $isCollapsed={isSidebarCollapsed}
              aria-hidden="true"
            >
              <ChevronDown />
            </SectionChevron>
          </SectionHeader>
          {!sectionCollapsed && items.map(renderItem)}
        </NavSection>
      </>
    );
  };

  return (
    <>
      <MobileOverlay $isOpen={isMobileSidebarOpen} onClick={closeMobileSidebar} />
      <Container $isCollapsed={isSidebarCollapsed} $isMobileOpen={isMobileSidebarOpen}>
        <Nav>
          {/* Personal "Main" section (Dashboard) — org users only.
              Super admins land on Platform Home and skip this section. */}
          {!isSuperAdmin() && renderSection('Main', filteredTopNav, false)}

          {renderSection('Workflows', workflowNavItems)}
          {renderSection('Insights', insightsNavItems)}
          {renderSection('Organization', filteredOrgNav)}
          {renderSection('System', filteredSystemNav)}
          {renderSection('Settings', filteredSettingsNav)}
        </Nav>
        {APP_VERSION && !isSidebarCollapsed && <SidebarFooter>Version {APP_VERSION}</SidebarFooter>}
      </Container>
    </>
  );
};

export default Sidebar;
